import {FC, useReducer} from "react";
import styled from "styled-components";
import {Trans, useTranslation} from "react-i18next";
import {NavLink} from "react-router-dom";
import {get} from "lodash";
import {Collapse} from "@mui/material";
import {getHeaderLogo} from "modules/utils";
import {SelectProjectTitle, subMenuProjectLinks, subMenuRouting} from "modules/constants";
import {Language} from "modules/enums";
import {INavLink} from "modules/types";
import {PureButton} from "components";
import {SubHeaderLink} from "./SubHeaderLink";
import {ReactComponent as Arrow} from "assets/images/icons/small-arrow.svg";

const Wrapper = styled.div`
	width: 100%;
	padding: 0 20px;
	background: ${({theme}) => theme.background.tertiary};
	color: ${({theme}) => theme.color.tertiary};
	display: none;

	${({theme}) => theme.breakpoints.up("mobile")} {
		display: block;
	}
`;

const Container = styled.div`
	max-width: 1240px;
	margin: 0 auto;
	display: flex;
	align-items: stretch;

	${({theme}) => theme.breakpoints.up("tablet")} {
		gap: 12px;
	}
`;

const Logo = styled.img`
	height: 50px;
	margin: 5px 24px 5px 0;
`;

const ProjectLinksContainer = styled.div`
	display: flex;
	flex-direction: column;
	position: relative;
	margin-left: auto;
	font-family: "Hind", sans-serif;
	font-weight: 600;
	font-size: 16px;
	line-height: 140%;
	color: ${({theme}) => theme.color.primary};
	z-index: 1;

	${({theme}) => theme.breakpoints.up("mobile")} {
		background: ${({theme}) => theme.background.primary};
	}
`;

const SelectProject = styled(PureButton)`
	display: flex;
	align-items: center;
	justify-content: space-between;
	height: 100%;
	padding: 10px;
	font: inherit;
	gap: 10px;

	${({theme}) => theme.breakpoints.up("mobile")} {
		min-width: 215px;
		background: #ffffff;

		svg path {
			fill: #202020;
		}
	}
`;

const SelectProjectText = styled.span`
	display: none;

	${({theme}) => theme.breakpoints.up("mobile")} {
		display: block;
	}
`;

const ProjectLink = styled.a`
	display: flex;
	background: ${({theme}) => theme.background.primary};
	width: 100%;
	padding: 10px;
	border-top: 1px solid #cccccc;
	white-space: nowrap;
	text-align: left;
`;

const ProjectLinks = styled.div`
	position: absolute;
	top: 100%;
	right: 0;
	width: 100%;

	@media (min-width: 769px) {
		right: 0;
		min-width: 140px;
	}
`;

export const SubHeader: FC = () => {
	const {t, i18n} = useTranslation();
	const logo = getHeaderLogo(i18n.language);
	const [isOpenProjectsLinks, toggleIsOpenProjectsLinks] = useReducer((isOpen) => !isOpen, false);
	const projectLinks = get(
		subMenuProjectLinks,
		i18n.language,
		subMenuProjectLinks[Language.English]
	) as INavLink[];

	return (
		<Wrapper className="sub-header">
			<Container>
				<NavLink to="/">
					<Logo src={logo} alt="CFL preseason futures" />
				</NavLink>

				{subMenuRouting.map((link) => (
					<SubHeaderLink key={link.title} {...link} />
				))}

				<ProjectLinksContainer>
					<SelectProject onClick={toggleIsOpenProjectsLinks}>
						<SelectProjectText>{t(SelectProjectTitle)}</SelectProjectText>
						<Arrow />
					</SelectProject>

					<ProjectLinks>
						<Collapse in={isOpenProjectsLinks}>
							{projectLinks.map(({title, href}) => (
								<ProjectLink key={href} href={href}>
									<Trans i18nKey={title} />
								</ProjectLink>
							))}
						</Collapse>
					</ProjectLinks>
				</ProjectLinksContainer>
			</Container>
		</Wrapper>
	);
};
